import { Injectable } from '@angular/core';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import type {
  ImportConfig,
  ImportColumnConfig,
  ImportMetadata
} from '../interfaces/import-config.interface';
import type {
  PreValidationResponse,
  PreValidationColumnError,
  PreValidatedRow
} from '../interfaces/pre-validation.interface';

/**
 * Pre-Validation Service
 *
 * Handles asynchronous pre-validation of Excel import data before final import.
 * Provides methods for:
 * - Executing pre-validation handlers
 * - Mapping backend responses to FormArray
 * - Populating related columns data
 * - Mapping validation errors to FormControls
 * - Filtering import payload to exclude related columns
 *
 * @example
 * ```typescript
 * constructor(private preValidationService: PreValidationService) {}
 *
 * async preValidate() {
 *   const result = await this.preValidationService.executePreValidation(
 *     this.config.preValidation!.handler,
 *     this.data,
 *     this.metadata,
 *     this.abortSignal
 *   );
 *
 *   this.preValidationService.mapResponseToFormArray(result, this.formArray, this.config);
 * }
 * ```
 */
@Injectable({
  providedIn: 'root'
})
export class PreValidationService {
  constructor() {}

  /**
   * Execute pre-validation handler with abort support
   *
   * Calls the provided handler function with data, metadata, and optional abort signal.
   * Handles errors gracefully, distinguishing between user cancellation (AbortError)
   * and actual errors (network, timeout, etc.).
   *
   * @param handler - Pre-validation handler function from config
   * @param data - All row data (including rows with frontend validation errors)
   * @param metadata - Import metadata (filename, sheet name, etc.)
   * @param signal - Optional AbortSignal for cancelling the request
   * @returns Promise resolving to PreValidationResponse
   * @throws Error if pre-validation fails (network error, timeout, etc.)
   *
   * @example
   * ```typescript
   * try {
   *   const result = await executePreValidation(handler, data, metadata, signal);
   *   console.log(`${result.validCount} rows validated`);
   * } catch (error) {
   *   if (error.name === 'AbortError') {
   *     console.log('Pre-validation cancelled');
   *   } else {
   *     console.error('Pre-validation failed:', error);
   *   }
   * }
   * ```
   */
  async executePreValidation(
    handler: (data: any[], metadata: ImportMetadata, signal?: AbortSignal) => Promise<PreValidationResponse>,
    data: any[],
    metadata: ImportMetadata,
    signal?: AbortSignal
  ): Promise<PreValidationResponse> {
    try {
      // Call the handler function with data, metadata, and signal
      const response = await handler(data, metadata, signal);

      // Validate response structure
      if (!response || typeof response !== 'object') {
        throw new Error('Invalid pre-validation response: Response must be an object');
      }

      if (!Array.isArray(response.rows)) {
        throw new Error('Invalid pre-validation response: rows must be an array');
      }

      return response;
    } catch (error: any) {
      // Handle AbortError separately - this is normal cancellation
      if (error.name === 'AbortError') {
        throw error; // Re-throw to let caller handle it
      }

      // Handle network errors, timeouts, HTTP errors
      const errorMessage = error.message || 'Unknown error during pre-validation';
      throw new Error(`Pre-validation failed: ${errorMessage}`);
    }
  }

  /**
   * Map pre-validation response to FormArray
   *
   * Updates FormGroups with:
   * - Related columns data from backend
   * - Validation errors from backend
   * - Internal pre-validation state flags
   *
   * @param response - Pre-validation response from backend
   * @param formArray - FormArray containing row FormGroups
   * @param config - Import configuration
   *
   * @example
   * ```typescript
   * const response = await preValidateData();
   * this.preValidationService.mapResponseToFormArray(response, this.formArray, this.config);
   * // FormArray now contains pre-validated data and errors
   * ```
   */
  mapResponseToFormArray(
    response: PreValidationResponse,
    formArray: FormArray,
    config: ImportConfig
  ): void {
    if (!formArray || formArray.length === 0) {
      console.warn('FormArray is empty or null, cannot map pre-validation response');
      return;
    }

    // Iterate through each row in the response
    response.rows.forEach((preValidatedRow: PreValidatedRow) => {
      const { rowIndex, isValid, errors, relatedColumnsData } = preValidatedRow;

      // Find corresponding FormGroup
      const formGroup = formArray.at(rowIndex) as FormGroup;

      if (!formGroup) {
        console.warn(`FormGroup not found at index ${rowIndex}, skipping`);
        return;
      }

      // 1. Apply related columns data
      this.applyRelatedColumnsData(relatedColumnsData, formGroup, config);

      // 2. Map errors to FormControls
      if (errors && errors.length > 0) {
        this.mapErrorsToFormControls(errors, formGroup);
      }

      // 3. Add internal pre-validation state flags
      // Check if control exists, if not create it
      if (!formGroup.contains('_isPreValidated')) {
        formGroup.addControl('_isPreValidated', new FormControl(true));
      } else {
        formGroup.get('_isPreValidated')?.setValue(true);
      }

      if (!formGroup.contains('_preValidationResult')) {
        formGroup.addControl('_preValidationResult', new FormControl(isValid ? 'success' : 'error'));
      } else {
        formGroup.get('_preValidationResult')?.setValue(isValid ? 'success' : 'error');
      }
    });
  }

  /**
   * Apply related columns data to FormGroup
   *
   * Populates FormControls with data from backend entity lookups.
   * Silently ignores unknown column keys (FR-8 requirement).
   *
   * @param relatedColumnsData - Data to populate (key-value pairs)
   * @param formGroup - FormGroup to update
   * @param config - Import configuration
   *
   * @example
   * ```typescript
   * const data = {
   *   client_name: 'Distribuidora ABC',
   *   client_city: 'Tegucigalpa',
   *   unknown_field: 'ignored'  // Will be silently ignored
   * };
   * this.applyRelatedColumnsData(data, formGroup, config);
   * ```
   */
  applyRelatedColumnsData(
    relatedColumnsData: { [columnKey: string]: any },
    formGroup: FormGroup,
    config: ImportConfig
  ): void {
    if (!relatedColumnsData || typeof relatedColumnsData !== 'object') {
      return;
    }

    // Iterate through each key in relatedColumnsData
    Object.keys(relatedColumnsData).forEach((columnKey: string) => {
      const value = relatedColumnsData[columnKey];

      // Check if FormControl exists for this key
      const formControl = formGroup.get(columnKey);

      if (formControl) {
        // Update value
        formControl.setValue(value, { emitEvent: false });

        // Mark as pristine (not dirty) since this is auto-populated data
        formControl.markAsPristine();
      }
      // If FormControl doesn't exist, silently ignore (FR-8)
    });
  }

  /**
   * Map pre-validation errors to FormControls
   *
   * Sets validation errors on specific FormControls.
   * Preserves existing errors by merging them with new pre-validation errors.
   *
   * @param errors - Array of pre-validation errors
   * @param formGroup - FormGroup to update
   *
   * @example
   * ```typescript
   * const errors = [{
   *   columnKey: 'codigo_cliente',
   *   columnName: 'Código Cliente',
   *   message: 'Cliente no encontrado',
   *   errorType: 'not_found'
   * }];
   * this.mapErrorsToFormControls(errors, formGroup);
   * ```
   */
  mapErrorsToFormControls(
    errors: PreValidationColumnError[],
    formGroup: FormGroup
  ): void {
    if (!errors || !Array.isArray(errors) || errors.length === 0) {
      return;
    }

    errors.forEach((error: PreValidationColumnError, index: number) => {
      const { columnKey, message, errorType, severity } = error;

      // Get FormControl
      let formControl = formGroup.get(columnKey);
      let targetColumnKey = columnKey;

      // If FormControl doesn't exist, attach to first available column (orphan error handling)
      if (!formControl) {
        const firstColumnKey = Object.keys(formGroup.controls).find(
          key => !key.startsWith('_') // Skip internal properties
        );

        if (firstColumnKey) {
          formControl = formGroup.get(firstColumnKey);
          targetColumnKey = firstColumnKey;

          console.warn(
            `[Pre-validation] Column "${columnKey}" not found. Attaching error to "${firstColumnKey}".`,
            `Error message: "${message}"`
          );
        } else {
          console.error(
            `[Pre-validation] No valid columns found in FormGroup to attach orphan error.`,
            `Error message: "${message}"`
          );
          return;
        }
      }

      if (formControl) {
        // Get existing errors (if any)
        const existingErrors = formControl.errors || {};

        // Create new error object with unique key to avoid conflicts
        // Include original column name in message if it's an orphan error
        const errorMessage = targetColumnKey !== columnKey
          ? `[${columnKey}] ${message}`
          : message;

        const newError = {
          [`preValidation${index}`]: {
            message: errorMessage,
            errorType,
            severity: severity || 'error'
          }
        };

        // Merge existing errors with new pre-validation error
        const mergedErrors = { ...existingErrors, ...newError };

        // Set merged errors
        formControl.setErrors(mergedErrors);

        // Mark as touched to trigger error display
        formControl.markAsTouched();
      }
    });
  }

  /**
   * Check if a column is a related column
   *
   * Determines if a column is auto-populated by an identifier (not user-entered).
   * Used to filter columns when sending import payload to backend.
   *
   * @param column - Column configuration to check
   * @param config - Import configuration
   * @returns true if column is a related column, false otherwise
   *
   * @example
   * ```typescript
   * const column = config.columns.find(c => c.key === 'client_name');
   * const isRelated = this.isRelatedColumn(column, config);
   * // true if 'client_name' is in some identifier's relatedColumns array
   * ```
   */
  isRelatedColumn(column: ImportColumnConfig, config: ImportConfig): boolean {
    if (!column || !config || !config.columns) {
      return false;
    }

    // Check if this column's key exists in any identifier column's relatedColumns array
    return config.columns.some((col: ImportColumnConfig) =>
      col.isIdentifier &&
      col.relatedColumns &&
      col.relatedColumns.includes(column.key)
    );
  }

  /**
   * Filter import payload to exclude related columns
   *
   * Creates a clean payload with only user-entered data (excludes auto-populated related columns).
   * This prevents sending redundant data back to the backend (FR-20).
   *
   * @param rows - Array of row data
   * @param config - Import configuration
   * @returns Filtered array with only non-related column data
   *
   * @example
   * ```typescript
   * const allRows = formArray.value; // Includes related columns
   * const filteredRows = this.filterImportPayload(allRows, config);
   * // filteredRows only contains user-entered data, not auto-populated fields
   * await this.apiService.import(filteredRows, metadata);
   * ```
   */
  filterImportPayload(rows: any[], config: ImportConfig): any[] {
    if (!rows || !Array.isArray(rows) || rows.length === 0) {
      return [];
    }

    // Get columns to send (exclude related columns)
    const columnsToSend = config.columns;
    // Map each row to only include non-related columns
    return rows.map((row: any) => {
      const filteredRow: any = {};

      columnsToSend.forEach((col: ImportColumnConfig) => {
        if (row.hasOwnProperty(col.key)) {
          filteredRow[col.key] = row[col.key];
        }
      });

      return filteredRow;
    });
  }
}
